// Domain types shared across the editor. Kept intentionally structural (no
// classes) so parsers, ops, and components can all pass plain objects around.

export type DxfUnit = "mm" | "cm" | "m" | "in" | "ft";
export type Theme = "dark" | "light";
export type SelectionMode = "inside" | "cross" | "outside";
export type SidebarPosition = "left" | "right";

// $INSUNITS integer codes we recognise. The values map to DxfUnit names.
export const UNIT_CODE: Record<number, DxfUnit> = {
  1: "in",
  2: "ft",
  4: "mm",
  5: "cm",
  6: "m",
};

// Millimetres per one unit of each supported DxfUnit. The universal metric
// lets us convert between any two units by dividing their mm-per-unit values.
export const MM_PER_UNIT: Record<DxfUnit, number> = {
  mm: 1,
  cm: 10,
  m: 1000,
  in: 25.4,
  ft: 304.8,
};

// Human-readable label for a DxfUnit ("Millimeters (mm)"). Used by the
// settings and export modals so both share the same wording.
export const UNIT_LABEL: Record<DxfUnit, string> = {
  mm: "Millimeters (mm)",
  cm: "Centimeters (cm)",
  m: "Meters (m)",
  in: "Inches (in)",
  ft: "Feet (ft)",
};

export interface Layer {
  name: string;
  color: string;
  visible: boolean;
  locked: boolean;
  entityIds: number[];
}

// The raw DXF entity we accept from the parser. Fields are optional so a single
// shape can carry LINE, ARC, CIRCLE, LWPOLYLINE, and POLYLINE without a union.
export interface RawEntity {
  type: "LINE" | "ARC" | "CIRCLE" | "LWPOLYLINE" | "POLYLINE";
  layer?: string;
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
  centerX?: number;
  centerY?: number;
  radius?: number;
  startAngle?: number;
  endAngle?: number;
  flags?: number;
  vertexCount?: number;
  // Polyline vertices with optional bulge (DXF group 42). Bulge encodes an
  // arc segment: `bulge = tan(theta/4)` where theta is the included angle
  // of the arc FROM this vertex TO the next; a bulge of 0 (or absent) means
  // a straight-line segment. `_add_polyline_with_bulge` in fix_dxf.py has
  // the reference geometry; extractPoints expands the same way for render.
  vertices?: Array<{ x: number; y: number; b?: number }>;
}

// The entity as the editor stores it: original DXF data plus a flattened
// point list for rendering + hit-testing.
export interface Entity {
  id: number;
  type: RawEntity["type"];
  layer: string;
  data: RawEntity;
  points: Array<[number, number]>;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface Pan {
  x: number;
  y: number;
}

// A single undo/redo checkpoint. We snapshot the mutable pieces only.
export interface HistoryEntry {
  action: string;
  timestamp: number;
  data: {
    entities: Entity[];
    layers: Record<string, Layer>;
    layerOrder: string[];
    selectedEntityIds: number[];
    selectedLayerNames: string[];
    selectedLayer: string | null;
    bounds: Bounds | null;
  };
}

// ---- UI-transient state ----

// A single toast entry in the persistent bottom-right stack. New entries
// are pushed on top; entries stay until the user explicitly dismisses them
// via the per-toast close button.
export interface ToastEntry {
  id: number;         // monotonically increasing, stable across renders
  message: string;
  timestamp: number;  // ms epoch — displayed as HH:MM:SS in the toast body
}

export type ModalKind =
  | "add-layer"
  | "rename-layer"
  | "move-to-layer"
  | "move-to-new-layer"
  | "unit-settings"
  | "export"
  | "confirm";

export interface ModalState {
  open: boolean;
  kind: ModalKind | null;
  // Free-form payload, shape depends on `kind`.
  data: Record<string, unknown>;
}

export interface ContextMenuState {
  open: boolean;
  x: number;
  y: number;
  // When set, the menu is scoped to a specific layer (right-click on a layer
  // row) and renders layer-scoped items instead of the general canvas menu.
  layer?: string;
}

// Where the box-select drag started + which mode it should apply.
export interface DragSelectState {
  active: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  mode: SelectionMode;
}
